<template>
  <div class="anime-site">
    <!-- 左侧导航栏 -->
    <AppSidebar />

    <!-- 主内容区域 -->
    <div class="main-content">
      <!-- 顶部搜索栏 -->
      <div class="header">
        <div class="search-container">
          <input 
            type="text" 
            class="search-input" 
            placeholder="请输入你想搜索的番剧名称" 
            v-model="searchKeyword"
            @keyup.enter="handleSearch"
          />
          <i class="icon search" @click="handleSearch"></i>
        </div>
        
      </div>

      <!-- 搜索结果区域 -->
      <div class="search-results" v-if="showSearchResults">
        <div class="section-header">
          <h2 class="section-title">搜索结果: {{ searchResults.keyword }}</h2>
          <button class="close-btn" @click="closeSearchResults">关闭</button>
        </div>
        <div class="search-results-content" v-if="searchResults.list.length > 0">
          <div class="anime-grid">
            <div class="anime-card" v-for="comic in searchResults.list" :key="comic.id">
              <router-link :to="`/comic/${comic.id}`" class="anime-link">
                <img :src="BASE_URL + comic.cover" :alt="comic.title" class="anime-cover" />
                <div class="anime-info">
                  <div class="anime-title">{{ comic.title }}</div>
                  <div class="anime-status">{{ comic.author }} | {{ comic.chapter_count }}话</div>
                  <div class="anime-description">{{ comic.description.substring(0, 50) }}...</div>
                </div>
              </router-link>
            </div>
          </div>
          <div class="pagination" v-if="searchResults.pages > 1">
            <button 
              class="page-btn" 
              :disabled="searchResults.page <= 1"
              @click="searchPage(searchResults.page - 1)"
            >&lt; 上一页</button>
            <span class="page-info">{{ searchResults.page }} / {{ searchResults.pages }}</span>
            <button 
              class="page-btn" 
              :disabled="searchResults.page >= searchResults.pages"
              @click="searchPage(searchResults.page + 1)"
            >下一页 &gt;</button>
          </div>
        </div>
        <div class="search-no-results" v-else>
          <p>未找到与 "{{ searchResults.keyword }}" 相关的漫画，请尝试其他关键词</p>
        </div>
      </div>

      <!-- 轮播图区域 -->
      <div class="carousel-container">
        <div class="carousel" @mouseenter="stopAutoplay" @mouseleave="startAutoplay">
          <div class="carousel-item" v-for="(slide, index) in slides" :key="index" :class="{ active: index === currentSlide }">
            <img :src="slide.image" :alt="slide.title" class="carousel-image" />
            <div class="carousel-info">
              <h2>{{ slide.title }}</h2>
              <p>{{ slide.info }}</p>
              <p>{{ slide.description }}</p>
            </div>
          </div>
          <div class="carousel-controls">
            <button class="prev-btn" @click="prevSlide">&lt;</button>
            <button class="next-btn" @click="nextSlide">&gt;</button>
          </div>
          <div class="carousel-indicators">
            <span 
              v-for="(slide, index) in slides" 
              :key="index" 
              class="indicator" 
              :class="{ active: index === currentSlide }"
              @click="setSlide(index)"
            ></span>
          </div>
          
          <!-- 右侧缩略图控制器 -->
          <div class="thumbnail-navigator">
            <div class="thumbnail-container" ref="thumbnailContainer">
              <div 
                v-for="(slide, index) in slides" 
                :key="index" 
                class="thumbnail" 
                :class="{ active: index === currentSlide }"
                @click="setSlide(index)"
                :ref="el => { if (el && index === currentSlide) activeThumbnail = el }"
              >
                <img :src="slide.image" :alt="slide.title" />
                <div class="thumbnail-title">{{ slide.title }}</div>
                <div class="thumbnail-info">{{ slide.info }}</div>
                <div class="thumbnail-desc" v-if="index === currentSlide">{{ slide.description }}</div>
                <div class="active-indicator" v-if="index === currentSlide"></div>
              </div>
            </div>
            <button class="thumbnail-nav-btn next">&gt;</button>
          </div>
        </div>
      </div>

      <!-- 追番周表区域 -->
      <div class="schedule-section">
        <h2 class="section-title">追番周表</h2>
        <div class="anime-grid">
          <div class="anime-card" v-for="(anime, index) in scheduleAnimes" :key="anime.id">
            <router-link :to="`/comic/${anime.id}`" class="anime-link">
              <div class="update-badge" v-if="index < 2">更新</div>
              <img :src="anime.cover" :alt="anime.title" class="anime-cover" />
              <div class="anime-info">
                <div class="anime-title">{{ anime.title }}</div>
                <div class="anime-status">{{ anime.status }} | {{ anime.chapter_count }}话</div>
                <div class="anime-meta">更新时间：{{ anime.updateTime }}</div>
                <div class="anime-meta">浏览次数：{{ anime.views }}</div>
              </div>
            </router-link>
          </div>
        </div>
      </div>

      <div class="promo-banner-section" @mouseenter="stopPromoAutoplay" @mouseleave="startPromoAutoplay">
        <div class="promo-banner-track" :style="{ transform: `translateX(-${promoCurrentSlide * 50}%)` }">
          <img :src="bannerImage" alt="横幅1" class="promo-banner-image" />
          <img :src="bannerImage1" alt="横幅2" class="promo-banner-image" />
        </div>
        <button class="promo-banner-btn prev" @click="prevPromoSlide">&lt;</button>
        <button class="promo-banner-btn next" @click="nextPromoSlide">&gt;</button>
        <div class="promo-banner-dots">
          <span class="promo-banner-dot" :class="{ active: promoCurrentSlide === 0 }" @click="setPromoSlide(0)"></span>
          <span class="promo-banner-dot" :class="{ active: promoCurrentSlide === 1 }" @click="setPromoSlide(1)"></span>
        </div>
      </div>

      <!-- TV番剧区域 -->
      <div class="tv-series-section">
        <div class="section-header">
          <h2 class="section-title">热推推荐</h2>
          <a href="#" class="more-link">更多 &gt;</a>
        </div>
        <div class="tv-series-grid">
          <div class="tv-series-card" v-for="anime in tvSeries" :key="anime.id">
            <router-link :to="`/comic/${anime.id}`" class="tv-link">
              <div class="tv-cover-container">
                <img :src="anime.cover" :alt="anime.title" class="tv-cover" />
                <div class="tv-episode-badge">{{ anime.episodeInfo }}</div>
              </div>
              <div class="tv-info">
                <div class="tv-title">{{ anime.title }}</div>
                <div class="tv-description">{{ anime.description }}</div>
                <div class="tv-meta">更新时间：{{ anime.updateTime }}</div>
                <div class="tv-meta">浏览次数：{{ anime.views }}</div>
              </div>
            </router-link>
          </div>
        </div>
      </div>

      <!-- 热门排行榜区域 -->
      <div class="ranking-section">
        <div class="section-header">
          <h2 class="section-title">热门排行榜</h2>
        </div>
        <div class="ranking-container">
          <div class="ranking-column ranking-card">
            <div class="ranking-header">
              <h3>热门排行榜</h3>
              <a href="#" class="more-link">更多 &gt;</a>
            </div>
            <ul class="ranking-list">
              <li v-for="(item, index) in rankingList.hotRanking" :key="item.id" class="ranking-item">
                <router-link :to="`/comic/${item.id}`" class="ranking-link">
                  <span class="ranking-number" :class="{'top-three': index < 3}">{{ index + 1 }}</span>
                  <div class="ranking-content">
                    <div class="ranking-title">{{ item.title }}</div>
                    <div class="ranking-info">{{ item.info }}</div>
                    <div class="ranking-meta">更新时间：{{ item.updateTime }}</div>
                    <div class="ranking-meta">浏览次数：{{ item.views }}</div>
                  </div>
                  <i class="icon arrow-right"></i>
                </router-link>
              </li>
    </ul>
          </div>
          <div class="ranking-column ranking-card">
            <div class="ranking-header">
              <h3>每周排行榜</h3>
              <a href="#" class="more-link">更多 &gt;</a>
            </div>
            <ul class="ranking-list">
              <li v-for="(item, index) in rankingList.weeklyRanking" :key="item.id" class="ranking-item">
                <router-link :to="`/comic/${item.id}`" class="ranking-link">
                  <span class="ranking-number" :class="{'top-three': index < 3}">{{ index + 1 }}</span>
                  <div class="ranking-content">
                    <div class="ranking-title">{{ item.title }}</div>
                    <div class="ranking-info">{{ item.info }}</div>
                    <div class="ranking-meta">更新时间：{{ item.updateTime }}</div>
                    <div class="ranking-meta">浏览次数：{{ item.views }}</div>
                  </div>
                  <i class="icon arrow-right"></i>
                </router-link>
              </li>
    </ul>
          </div>
        </div>
      </div>

      <!-- 页脚区域 -->
      <AppFooter />
    </div>

    <!-- 右侧控制栏 -->
    <div class="right-controls">
      <button class="control-btn theme-btn" @click="toggleTheme" title="切换主题">
       <svg t="1751615093460" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="5768" width="220" height="220"><path d="M925.1 338.1c-22.6-53.3-54.8-101.2-96-142.3s-89-73.4-142.3-96c-55.2-23.4-113.9-35.2-174.3-35.2S393.3 76.4 338.1 99.8c-53.3 22.6-101.2 54.8-142.3 96s-73.4 89-96 142.3C76.4 393.3 64.6 452 64.6 512.4c0 25 5.6 47.3 16.6 66.4 10 17.3 24.1 31.8 43.2 44.5 32.6 21.6 73.6 33.8 117 46.7 46.1 13.7 93.8 27.9 127.5 53.7 23.9 18.3 32.9 61.3 41.5 102.9 6.2 30 12.7 61 25.5 85.3 16.9 32.1 42.6 48.3 76.6 48.3 60.4 0 119.1-11.8 174.3-35.2 53.3-22.6 101.2-54.8 142.3-96s73.4-89 96-142.3c23.4-55.2 35.2-113.9 35.2-174.3s-11.9-119.1-35.2-174.3zM879 667.3c-20 47.4-48.7 89.9-85.3 126.5S714.6 859 667.3 879c-49 20.7-101.1 31.3-154.8 31.3-30.4 0-39.5-28.6-53.1-93.8-10-48.2-21.4-102.9-60.1-132.4-41-31.3-93.2-46.9-143.6-61.8-39.7-11.8-77.2-23-103.6-40.5-25.9-17.2-37.5-38.6-37.5-69.3 0-53.7 10.5-105.8 31.3-154.8 20-47.4 48.7-89.9 85.3-126.5s79.1-65.2 126.5-85.3c49-20.7 101.1-31.3 154.8-31.3 53.7 0 105.8 10.5 154.8 31.3 47.4 20 89.9 48.7 126.5 85.3s65.2 79 85.2 126.4c20.7 49 31.3 101.1 31.3 154.8 0 53.7-10.6 105.8-31.3 154.9z" p-id="5769"></path><path d="M614.8 640.4c-50 0-90.6 40.7-90.6 90.6s40.7 90.6 90.6 90.6 90.6-40.7 90.6-90.6-40.6-90.6-90.6-90.6z m0 131.3c-22.4 0-40.6-18.2-40.6-40.6s18.2-40.6 40.6-40.6 40.6 18.2 40.6 40.6-18.2 40.6-40.6 40.6z" p-id="5770"></path><path d="M320.1 320m-46.2 0a46.2 46.2 0 1 0 92.4 0 46.2 46.2 0 1 0-92.4 0Z" p-id="5771"></path><path d="M447.8 256m-46.2 0a46.2 46.2 0 1 0 92.4 0 46.2 46.2 0 1 0-92.4 0Z" p-id="5772"></path><path d="M575.6 256m-46.2 0a46.2 46.2 0 1 0 92.4 0 46.2 46.2 0 1 0-92.4 0Z" p-id="5773"></path><path d="M703.3 319.9m-46.2 0a46.2 46.2 0 1 0 92.4 0 46.2 46.2 0 1 0-92.4 0Z" p-id="5774"></path></svg>
      </button>
      <button class="control-btn feedback" @click="showFeedback" title="意见反馈">
        <svg t="1751615114217" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="6802" width="220" height="220"><path d="M730.9 175.9v93c0 22.4-18.2 40.6-40.6 40.6H268.9V117.6h403.8c32.1 0 58.2 26.1 58.2 58.3z" fill="#1A6DFF" p-id="6803"></path><path d="M690.3 317.6H260.7V109.5h412c36.6 0 66.4 29.8 66.4 66.5v93c0 26.8-21.9 48.6-48.8 48.6z m-413.2-16.3h413.3c17.9 0 32.4-14.5 32.4-32.4v-93c0-27.6-22.4-50.1-50-50.1H277.1v175.5z" fill="#282828" p-id="6804"></path><path d="M94.9 769v35.7c0 22.4 18.2 40.6 40.6 40.6H557V710.7H153.1c-32.1 0-58.2 26.1-58.2 58.3z" fill="#1A6DFF" p-id="6805"></path><path d="M565.1 853.5H135.5c-26.9 0-48.7-21.9-48.7-48.7V769c0-36.7 29.8-66.5 66.4-66.5h412v151z m-412-134.6c-27.6 0-50 22.5-50 50.1v35.7c0 17.9 14.5 32.4 32.4 32.4h413.3V718.9H153.1z" fill="#282828" p-id="6806"></path><path d="M253.6 117.6c-16.1 0-30.7 6.5-41.2 17.1-10.5 10.5-17.1 25.1-17.1 41.2v611.4c0 3.1-0.2 6.2-0.8 9.2-1.9 12.4-7.8 23.5-16.3 32-10.5 10.5-25 17.1-41.1 17.1h420.5c16.1 0 30.7-6.5 41.1-17.1 8.5-8.4 14.4-19.5 16.3-32V175.9c0-32.2 26.1-58.3 58.3-58.3H253.6z" fill="#FFFFFF" p-id="6807"></path><path d="M557.6 853.6H137.1c-4.5 0-8.2-3.7-8.2-8.2 0-4.5 3.7-8.2 8.2-8.2 13.4 0 25.9-5.2 35.3-14.7 7.6-7.5 12.4-17 14-27.4 0.5-2.6 0.7-5.2 0.7-7.9V175.9c0-17.7 6.9-34.4 19.5-47s29.3-19.5 47-19.5h419.7c4.5 0 8.2 3.7 8.2 8.2s-3.7 8.2-8.2 8.2c-27.6 0-50.1 22.5-50.1 50.1v620.5c0 0.4 0 0.8-0.1 1.2-2.1 14-8.5 26.6-18.6 36.6-12.4 12.5-29.1 19.4-46.9 19.4z m-376.9-16.3h377c13.4 0 25.9-5.2 35.3-14.7 7.5-7.4 12.2-16.6 13.9-26.9V175.9c0-20 8.9-37.9 22.9-50.1H253.6c-13.4 0-26 5.2-35.4 14.7-9.5 9.5-14.7 22.1-14.7 35.4v611.3c0 3.8-0.3 7.4-1 10.7-2.1 13.6-8.5 26.3-18.5 36.2-1.1 1.1-2.2 2.2-3.3 3.2z" fill="#282828" p-id="6808"></path><path d="M533.7 221.7H274.1c-4.5 0-8.2-3.7-8.2-8.2s3.7-8.2 8.2-8.2h259.6c4.5 0 8.2 3.7 8.2 8.2s-3.7 8.2-8.2 8.2zM533.7 307.1H274.1c-4.5 0-8.2-3.7-8.2-8.2s3.7-8.2 8.2-8.2h259.6c4.5 0 8.2 3.7 8.2 8.2s-3.7 8.2-8.2 8.2zM533.7 392.4H274.1c-4.5 0-8.2-3.7-8.2-8.2s3.7-8.2 8.2-8.2h259.6c4.5 0 8.2 3.7 8.2 8.2s-3.7 8.2-8.2 8.2zM485.7 477.8H274.1c-4.5 0-8.2-3.7-8.2-8.2s3.7-8.2 8.2-8.2h211.6c4.5 0 8.2 3.7 8.2 8.2s-3.7 8.2-8.2 8.2zM485.7 563.2H274.1c-4.5 0-8.2-3.7-8.2-8.2 0-4.5 3.7-8.2 8.2-8.2h211.6c4.5 0 8.2 3.7 8.2 8.2-0.1 4.5-3.7 8.2-8.2 8.2zM814.3 710.4H602.7c-4.5 0-8.2-3.7-8.2-8.2s3.7-8.2 8.2-8.2h211.6c4.5 0 8.2 3.7 8.2 8.2s-3.7 8.2-8.2 8.2zM502.3 698.5H383.7c-4.5 0-8.2-3.7-8.2-8.2 0-4.5 3.7-8.2 8.2-8.2h118.6c4.5 0 8.2 3.7 8.2 8.2 0 4.5-3.6 8.2-8.2 8.2zM502.3 764.8H383.7c-4.5 0-8.2-3.7-8.2-8.2 0-4.5 3.7-8.2 8.2-8.2h118.6c4.5 0 8.2 3.7 8.2 8.2 0 4.6-3.6 8.2-8.2 8.2z" fill="#282828" p-id="6809"></path><path d="M870.7 763.5V889c0 10.3-8.3 18.7-18.7 18.7H565c-10.3 0-18.7-8.4-18.7-18.7V763.5c0-10.4 8.4-18.8 18.7-18.8h287c10.4 0 18.7 8.4 18.7 18.8z" fill="#FFFFFF" p-id="6810"></path><path d="M852 916H565c-14.8 0-26.9-12.1-26.9-26.9V763.5c0-14.9 12.1-27 26.9-27h287c14.8 0 26.9 12.1 26.9 27V889c0 14.9-12.1 27-26.9 27zM565 752.9c-5.8 0-10.5 4.8-10.5 10.6V889c0 5.8 4.7 10.5 10.5 10.5h287c5.8 0 10.5-4.7 10.5-10.5V763.5c0-5.9-4.7-10.6-10.5-10.6H565z" fill="#282828" p-id="6811"></path><path d="M844.8 783.1v101c0 9-7.3 16.3-16.3 16.3H579c-9 0-16.3-7.3-16.3-16.3v-101c0-9 7.3-16.4 16.3-16.4h249.6c9 0.1 16.2 7.4 16.2 16.4z" fill="#50BCFF" p-id="6812"></path><path d="M832.2 744.7H584.8l34.4-30.6s10.9-11.9 20.8-11.9h140.1c6.5 0 10.9 3.5 15.5 8.3 0 0 4.3 4.4 4.3 4.5l32.3 29.7z" fill="#FFFFFF" p-id="6813"></path><path d="M832.2 752.9H584.8c-3.4 0-6.4-2.1-7.6-5.3-1.2-3.2-0.3-6.8 2.2-9l34.1-30.3c2.4-2.5 14.1-14.3 26.6-14.3h140.1c9.6 0 16 5.2 21.4 10.7 1.8 1.8 3.1 3.1 4 4.2l32.1 29.8c2.5 2.3 3.3 5.9 2.1 9-1.2 3.1-4.3 5.2-7.6 5.2z m-225.9-16.3h205L794.5 721l-0.7-0.7c-0.8-0.9-3.9-4-3.9-4-4.3-4.4-6.7-5.8-9.7-5.8H640.1c-4.5 0-11.9 6-14.8 9.3l-0.6 0.6-18.4 16.2z" fill="#282828" p-id="6814"></path><path d="M622.6 597.3l-101.2 98.3s43.2-19.6 65.8-14.2l65.8-71.7-30.4-12.4z" fill="#FFFFFF" p-id="6815"></path><path d="M521.5 703.8c-2.6 0-5.2-1.3-6.8-3.6-2.2-3.3-1.8-7.7 1.1-10.5L617 591.4c2.3-2.3 5.8-2.9 8.8-1.7l30.4 12.3c2.5 1 4.3 3.2 4.9 5.8 0.6 2.6-0.1 5.4-1.9 7.3l-65.8 71.7c-2 2.2-5 3.2-8 2.4-16.3-3.9-49.2 8.6-60.5 13.7-1.2 0.6-2.3 0.9-3.4 0.9z m103-96.9l-70.9 68.8c10.3-2.6 21.3-4.2 30.6-3.2l54.8-59.8-14.5-5.8z" fill="#282828" p-id="6816"></path><path d="M903.4 276.3s-267 189.3-316.2 333l46.3 37.2s89.8-39.6 286.7-350.9c16.5-26.1 13.8-37.3-16.8-19.3z" fill="#1A6DFF" p-id="6817"></path><path d="M632.3 655.9L577.6 612l1.9-5.4c49.5-144.5 308.2-329.2 319.2-337l0.6-0.4c13.1-7.7 27.5-14.4 35.6-6.1 8.3 8.6 0.2 24.1-7.7 36.7-195.9 309.9-286.5 352.4-290.3 354l-4.6 2.1z m-35.4-49.4l37.5 30.1c19.7-12.2 109.9-78.1 279-345.5 4.4-7 6.6-11.6 7.7-14.5-2.7 1-6.9 2.8-13.1 6.5-10 7.1-259.3 186.6-311.1 323.4z" fill="#282828" p-id="6818"></path></svg>
      </button>
    </div>

    <!-- 反馈弹窗 -->
    <div class="feedback-modal" v-if="showFeedbackModal">
      <div class="feedback-content">
        <div class="feedback-header">
          <h3>意见反馈</h3>
          <button class="close-btn" @click="closeFeedback">&times;</button>
        </div>
        <div class="feedback-body">
          <textarea 
            v-model="feedbackContent" 
            placeholder="请输入您的意见或建议，我们将尽快处理..." 
            rows="5"
            class="feedback-textarea"
          ></textarea>
          <div class="feedback-contact">
            <input 
              type="text" 
              v-model="contactInfo" 
              placeholder="联系方式（选填）" 
              class="contact-input"
            />
          </div>
        </div>
        <div class="feedback-footer">
          <button class="submit-btn" @click="submitFeedback" :disabled="submitting">
            <span v-if="submitting">提交中...</span>
            <span v-else>提交反馈</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import bannerImage from '../assets/lunbo.png';
import bannerImage1 from '../assets/lunbo1.png';
import { getBanners } from '../api/banner';
import { getRandomComics, getHotRecommendComics, getHotRankingComics, getWeeklyRankingComics, searchComics } from '../api/comic';
import { BASE_URL } from '../api/config';
import AppSidebar from './layout/Sidebar.vue';
import AppFooter from './layout/Footer.vue';

export default {
  name: 'HelloWorld',
  components: {
    AppSidebar,
    AppFooter
  },
  props: {
    msg: String
  },
  data() {
    return {
      BASE_URL,
      bannerImage,
      bannerImage1,
      promoCurrentSlide: 0,
      currentSlide: 0,
      slides: [],
      tvSeries: [],
      rankingList: {
        hotRanking: [],
        weeklyRanking: []
      },
      autoplayInterval: null,
      promoAutoplayInterval: null,
      isUserScrolling: false,
      scrollTimeout: null,
      activeThumbnail: null,
      // 追番周表数据
      scheduleAnimes: [],
      searchKeyword: '',
      showSearchResults: false,
      searchResults: {
        keyword: '',
        list: [],
        total: 0,
        page: 1,
        pages: 0
      },
      // 右侧控制栏相关数据
      isDarkTheme: false,
      showFeedbackModal: false,
      feedbackContent: '',
      contactInfo: '',
      submitting: false
    }
  },
  async mounted() {
    // 获取轮播图数据
    await this.fetchBanners();
    
    // 获取漫画数据用于追番周表
    await this.fetchComics();
    
    // 获取热推推荐数据
    await this.fetchHotRecommendComics();
    
    // 获取排行榜数据
    await this.fetchRankingData();
    
    // 启动自动轮播
    this.startAutoplay();
    this.startPromoAutoplay();
    
    // 监听滚动事件
    window.addEventListener('scroll', this.handleScroll);

    // 检查并应用保存的主题设置
    const savedTheme = localStorage.getItem('darkTheme');
    if (savedTheme === '1') {
      this.isDarkTheme = true;
      document.body.classList.add('dark-theme');
    }
  },
  beforeUnmount() {
    // 组件销毁前清除定时器和事件监听
    this.stopAutoplay();
    this.stopPromoAutoplay();
    window.removeEventListener('scroll', this.handleScroll);
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  },
  methods: {
    // 获取轮播图数据
    async fetchBanners() {
      try {
        const banners = await getBanners();
        
        if (banners && banners.length > 0) {
          // 将API返回的数据转换为组件需要的格式
          this.slides = banners.map(banner => ({
            image: banner.image_url,
            title: banner.title,
           
            description: banner.title
          }));
        }
      } catch (error) {
        console.error('获取轮播图失败:', error);
      }
    },
    
    // 获取漫画数据
    async fetchComics() {
      try {
        const comics = await getRandomComics(6);

        if (comics && comics.length > 0) {
          // 将API返回的数据转换为组件需要的格式
          this.scheduleAnimes = comics.map(comic => ({
            id: comic.id,
            title: comic.title,
            cover: `${BASE_URL}${comic.cover}`,
            description: comic.description,
            status: comic.status_text,
            chapter_count: comic.chapter_count,
            views: comic.views || 0,
            updateTime: this.formatTimestamp(comic.updatetime)
          }));
        }
      } catch (error) {
        console.error('获取漫画数据失败:', error);
      }
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp * 1000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    // 获取热推推荐数据
    async fetchHotRecommendComics() {
      try {
        const comics = await getHotRecommendComics(16);
        
        if (comics && comics.length > 0) {
          // 将API返回的数据转换为组件需要的格式
          this.tvSeries = comics.map(comic => ({
            id: comic.id,
            title: comic.title,
            cover: `${BASE_URL}${comic.cover}`,
            description: comic.status_text,
            episodeInfo: `${comic.chapter_count}话`,
            views: comic.views || 0,
            updateTime: this.formatTimestamp(comic.updatetime)
          }));
        }
      } catch (error) {
        console.error('获取热推推荐数据失败:', error);
      }
    },
    
    // 获取排行榜数据
    async fetchRankingData() {
      try {
        // 获取热门排行榜数据
        const hotRankingComics = await getHotRankingComics(10);
        if (hotRankingComics && hotRankingComics.length > 0) {
          this.rankingList.hotRanking = hotRankingComics.map(comic => ({
            id: comic.id,
            title: comic.title,
            info: `${comic.status_text} | ${comic.chapter_count}话`,
            views: comic.views || 0,
            updateTime: this.formatTimestamp(comic.updatetime)
          }));
        }
        
        // 获取每周排行榜数据
        const weeklyRankingComics = await getWeeklyRankingComics(10);
        if (weeklyRankingComics && weeklyRankingComics.length > 0) {
          this.rankingList.weeklyRanking = weeklyRankingComics.map(comic => ({
            id: comic.id,
            title: comic.title,
            info: `${comic.status_text} | ${comic.chapter_count}话`,
            views: comic.views || 0,
            updateTime: this.formatTimestamp(comic.updatetime)
          }));
        }
      } catch (error) {
        console.error('获取排行榜数据失败:', error);
      }
    },
    
    startAutoplay() {
      this.autoplayInterval = setInterval(() => {
        // 只有在用户不滚动时才自动切换
        if (!this.isUserScrolling) {
          this.nextSlide();
        }
      }, 5000); // 每5秒切换一次
    },
    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
      }
    },
    startPromoAutoplay() {
      this.stopPromoAutoplay();
      this.promoAutoplayInterval = setInterval(() => {
        this.nextPromoSlide();
      }, 3000);
    },
    stopPromoAutoplay() {
      if (this.promoAutoplayInterval) {
        clearInterval(this.promoAutoplayInterval);
        this.promoAutoplayInterval = null;
      }
    },
    setPromoSlide(index) {
      this.promoCurrentSlide = index;
      this.startPromoAutoplay();
    },
    prevPromoSlide() {
      this.promoCurrentSlide = this.promoCurrentSlide === 0 ? 1 : 0;
      this.startPromoAutoplay();
    },
    nextPromoSlide() {
      this.promoCurrentSlide = this.promoCurrentSlide === 0 ? 1 : 0;
    },
    setSlide(index) {
      // 切换到指定索引的轮播图
      this.currentSlide = index;
      // 重置自动轮播计时器
      this.stopAutoplay();
      this.startAutoplay();
    },
    nextSlide() {
      // 切换到下一张轮播图
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    },
    prevSlide() {
      // 切换到上一张轮播图
      this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    },
    scrollToActive() {
      // 使用更安全的方法滚动缩略图，不影响页面滚动
      this.$nextTick(() => {
        if (this.activeThumbnail && this.$refs.thumbnailContainer) {
          const container = this.$refs.thumbnailContainer;
          const thumbnail = this.activeThumbnail;
          
          // 确保两个元素都存在且有效
          if (!container || !thumbnail) return;
          
          // 计算滚动位置
          const containerRect = container.getBoundingClientRect();
          const thumbnailRect = thumbnail.getBoundingClientRect();
          
          // 如果缩略图不在可视区域内，才滚动
          if (thumbnailRect.top < containerRect.top || 
              thumbnailRect.bottom > containerRect.bottom) {
            
            // 计算需要滚动的距离
            const scrollTop = thumbnail.offsetTop - container.offsetTop - 
                             (container.clientHeight / 2) + (thumbnail.clientHeight / 2);
            
            // 使用平滑滚动
            container.scrollTo({
              top: scrollTop,
              behavior: 'smooth'
            });
          }
        }
      });
    },
    handleScroll() {
      // 用户正在滚动
      this.isUserScrolling = true;
      
      // 清除之前的定时器
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      
      // 设置新的定时器，滚动停止1秒后恢复自动轮播
      this.scrollTimeout = setTimeout(() => {
        this.isUserScrolling = false;
      }, 1000);
    },
    // 搜索相关方法
    async handleSearch() {
      if (!this.searchKeyword || this.searchKeyword.trim() === '') {
        // 如果搜索关键词为空，不执行搜索
        return;
      }
      
      try {
        // 显示搜索结果区域
        this.showSearchResults = true;
        
        // 调用搜索API
        const results = await searchComics(this.searchKeyword, 1, 10);
        
        // 更新搜索结果数据
        this.searchResults = results;
      } catch (error) {
        console.error('搜索失败:', error);
        // 显示搜索失败信息
        this.searchResults = {
          keyword: this.searchKeyword,
          list: [],
          total: 0,
          page: 1,
          pages: 0
        };
      }
    },
    
    async searchPage(page) {
      try {
        // 调用搜索API进行分页
        const results = await searchComics(this.searchResults.keyword, page, 10);
        
        // 更新搜索结果数据
        this.searchResults = results;
        
        // 滚动到搜索结果顶部
        const searchResultsEl = document.querySelector('.search-results');
        if (searchResultsEl) {
          searchResultsEl.scrollIntoView({ behavior: 'smooth' });
        }
      } catch (error) {
        console.error('搜索分页失败:', error);
      }
    },
    
    closeSearchResults() {
      this.showSearchResults = false;
      // 清空搜索关键词和结果
      this.searchKeyword = '';
      this.searchResults = {
        keyword: '',
        list: [],
        total: 0,
        page: 1,
        pages: 0
      };
    },

    // 右侧控制栏相关方法
    scrollToTop() {
      // 使用多种方式确保滚动到顶部有效
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      
      // 兼容性处理，确保在所有浏览器中都能工作
      document.body.scrollTop = 0; // 对于Safari
      document.documentElement.scrollTop = 0; // 对于Chrome, Firefox, IE 和 Opera
    },
    
    toggleTheme() {
      this.isDarkTheme = !this.isDarkTheme;
      document.body.classList.toggle('dark-theme', this.isDarkTheme);
      
      // 保存主题设置到本地存储
      localStorage.setItem('darkTheme', this.isDarkTheme ? '1' : '0');
    },
    
    showFeedback() {
      this.showFeedbackModal = true;
    },
    
    closeFeedback() {
      this.showFeedbackModal = false;
      this.feedbackContent = '';
      this.contactInfo = '';
    },
    
    async submitFeedback() {
      if (!this.feedbackContent.trim()) {
        alert('请输入反馈内容');
        return;
      }
      
      this.submitting = true;
      
      try {
        // 这里可以添加实际的API调用
        // const response = await submitFeedback(this.feedbackContent, this.contactInfo);
        
        // 模拟API调用延迟
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        alert('感谢您的反馈，我们会尽快处理！');
        this.closeFeedback();
      } catch (error) {
        console.error('提交反馈失败:', error);
        alert('提交失败，请稍后再试');
      } finally {
        this.submitting = false;
      }
    }
  },
  watch: {
    currentSlide() {
      // 当轮播图切换时，滚动缩略图但不影响页面
      this.scrollToActive();
    }
  }
}
</script>

<style scoped>
/* 全局样式 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.anime-site {
  display: flex;
  min-height: 100vh;
}

/* 图标占位样式 */
.icon {
  display: inline-block;
  width: 20px;
  height: 20px;
  margin-right: 8px;
  background-color: #ccc;
  border-radius: 4px;
  vertical-align: middle;
}

/* 左侧导航栏样式 */
.sidebar {
  width: 160px;
  background-color: white;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  z-index: 10;
}

.logo {
  color: #ff6b81;
  font-size: 20px;
  font-weight: bold;
  padding: 10px 20px;
  margin-bottom: 20px;
}

.nav-menu {
  list-style: none;
}

.nav-menu li {
  padding: 12px 20px;
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
}

.nav-menu li:hover {
  background-color: #f8f9fa;
}

.nav-menu li.active {
  color: #ff6b81;
  background-color: #fff0f3;
}

.nav-menu li.active::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 3px;
  background-color: #ff6b81;
}

.nav-menu li.divider {
  height: 1px;
  background-color: #eee;
  margin: 10px 0;
  padding: 0;
}

.badge {
  background-color: #ff6b81;
  color: white;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 5px;
}

/* 主内容区域样式 */
.main-content {
  margin-left: 200px; /* 与侧边栏宽度相同 */
  width: calc(100% - 200px);
  padding: 20px;
  background-color: #f5f5f5;
}

/* 顶部搜索栏样式 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-container {
  position: relative;
  width: 50%;
}

.search-input {
  width: 100%;
  padding: 10px 15px;
  border-radius: 20px;
  border: none;
  background-color: #eaeaea;
  font-size: 14px;
}

.icon.search {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
}

.user-actions {
  display: flex;
  align-items: center;
}

.btn-history {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  margin-right: 15px;
  cursor: pointer;
  color: #666;
}

/* 轮播图区域样式 */
.carousel-container {
  margin-bottom: 30px;
}

.carousel {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  height: 400px;
  background-color: #000;
}

.carousel-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
  z-index: 1;
}

.carousel-item.active {
  opacity: 1;
  z-index: 2;
}

.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.8;
}

.carousel-info {
  position: absolute;
  bottom: 50px;
  left: 50px;
  color: white;
  max-width: 50%;
  z-index: 3;
}

.carousel-info h2 {
  font-size: 32px;
  margin-bottom: 10px;
}

.carousel-info p {
  font-size: 14px;
  margin-bottom: 5px;
  opacity: 0.9;
}

.carousel-controls {
  position: absolute;
  top: 50%;
  width: 100%;
  display: flex;
  justify-content: space-between;
  transform: translateY(-50%);
  z-index: 4;
}

.carousel-controls button {
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.prev-btn {
  margin-left: 20px;
}

.next-btn {
  margin-right: 20px;
}

.carousel-indicators {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  z-index: 4;
}

.indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  margin: 0 5px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator.active {
  background-color: white;
  width: 20px;
  border-radius: 4px;
}

/* 右侧缩略图导航器样式 */
.thumbnail-navigator {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 240px;
  background: linear-gradient(to right, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.7));
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 5;
}

.thumbnail-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 20px;
  max-height: 80%;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}

.thumbnail-container::-webkit-scrollbar {
  width: 5px;
}

.thumbnail-container::-webkit-scrollbar-track {
  background: transparent;
}

.thumbnail-container::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 10px;
}

.thumbnail {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 10px;
  border-radius: 8px;
  background-color: rgba(0, 0, 0, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 100px;
  flex-shrink: 0;
}

.thumbnail.active {
  background-color: rgba(255, 107, 129, 0.4);
}

.thumbnail img {
  width: 100%;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  margin-bottom: 5px;
}

.thumbnail-title {
  color: white;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 2px;
}

.thumbnail-info {
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  margin-bottom: 2px;
}

.thumbnail-desc {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.active-indicator {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 3px;
  background-color: #ff6b81;
  border-radius: 3px 0 0 3px;
}

.thumbnail-nav-btn {
  position: absolute;
  bottom: 20px;
  right: 20px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 追番周表区域样式 */
.schedule-section {
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
}

.promo-banner-section {
  position: relative;
  margin-bottom: 30px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
}

.promo-banner-track {
  display: flex;
  width: 200%;
  transition: transform 0.5s ease;
}

.promo-banner-image {
  display: block;
  width: 50%;
  height: auto;
  flex-shrink: 0;
}

.promo-banner-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  z-index: 2;
}

.promo-banner-btn.prev {
  left: 16px;
}

.promo-banner-btn.next {
  right: 16px;
}

.promo-banner-dots {
  position: absolute;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 2;
}

.promo-banner-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.55);
  cursor: pointer;
}

.promo-banner-dot.active {
  background: #fff;
}

.section-title {
  font-size: 18px;
  margin-bottom: 20px;
  font-weight: bold;
}

.anime-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 15px;
}

.anime-card {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 3/4;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.anime-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.update-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: #ff6b81;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.anime-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px;
}

.anime-title {
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.anime-status {
  font-size: 10px;
  color: #ccc;
  margin-bottom: 4px;
}

.anime-meta {
  font-size: 10px;
  color: #e5e5e5;
  line-height: 1.4;
}

/* TV番剧区域样式 */
.tv-series-section {
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.more-link {
  color: #666;
  text-decoration: none;
  font-size: 14px;
}

.tv-series-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  grid-template-rows: repeat(2, auto);
  gap: 15px;
}

.tv-series-card {
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.tv-cover-container {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 3/4;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 8px;
}

.tv-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.tv-episode-badge {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 12px;
  padding: 4px 8px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tv-info {
  display: flex;
  flex-direction: column;
}

.tv-title {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tv-description {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.tv-meta {
  font-size: 11px;
  color: #888;
  line-height: 1.4;
}

/* 右侧控制栏样式 */
.right-controls {
  position: fixed;
  right: 20px;
  bottom: 100px;
  display: flex;
  flex-direction: column;
  z-index: 1001; /* 提高z-index确保在最上层 */
}

.control-btn {
  width: 48px; /* 增加按钮尺寸 */
  height: 48px; /* 增加按钮尺寸 */
  border-radius: 50%;
  background-color: white;
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.control-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.control-btn .icon {
  margin: 0;
  width: 24px; /* 增加图标尺寸 */
  height: 24px; /* 增加图标尺寸 */
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.icon.theme::before {
  content: "🌓";
}

.icon.top::before {
  content: "⬆️";
}

.icon.feedback::before {
  content: "📝";
}

/* 反馈弹窗样式 */
.feedback-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.feedback-content {
  width: 400px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.feedback-header {
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.feedback-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
}

.feedback-body {
  padding: 20px;
}

.feedback-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  resize: none;
  font-size: 14px;
}

.contact-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-top: 10px;
  font-size: 14px;
}

.feedback-footer {
  padding: 15px 20px;
  border-top: 1px solid #eee;
  text-align: right;
}

.submit-btn {
  background-color: #ff6b81;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.submit-btn:hover:not(:disabled) {
  background-color: #ff5972;
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* 暗黑主题样式 */
body.dark-theme {
  background-color: #121212;
  color: #e0e0e0;
}

body.dark-theme .main-content {
  background-color: #121212;
}

body.dark-theme .section-title {
  color: #e0e0e0;
}

body.dark-theme .tv-series-card,
body.dark-theme .anime-card,
body.dark-theme .ranking-card {
  background-color: #1e1e1e;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
}

body.dark-theme .tv-title,
body.dark-theme .anime-title,
body.dark-theme .ranking-title {
  color: #e0e0e0;
}

body.dark-theme .tv-description,
body.dark-theme .anime-info,
body.dark-theme .ranking-info {
  color: #aaa;
}

body.dark-theme .control-btn {
  background-color: #333;
  color: #e0e0e0;
}

body.dark-theme .ranking-header {
  border-bottom-color: #333;
}

body.dark-theme .ranking-item {
  border-bottom-color: #333;
}

body.dark-theme .ranking-item:hover {
  background-color: #2a2a2a;
}

/* 热门排行榜区域样式 */
.ranking-section {
  margin-bottom: 30px;
}

.ranking-container {
  display: flex;
  gap: 20px;
}

.ranking-column {
  flex: 1;
}

.ranking-card {
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
}

.ranking-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.ranking-header h3 {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.ranking-list {
  list-style: none;
}

.ranking-item {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
}

.ranking-item:hover {
  background-color: #f9f9f9;
}

.ranking-number {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  color: #999;
  margin-right: 10px;
}

.ranking-number.top-three {
  background-color: #ff6b81;
  color: white;
  border-radius: 4px;
}

.ranking-content {
  flex: 1;
}

.ranking-title {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ranking-info {
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.ranking-meta {
  font-size: 11px;
  color: #aaa;
  line-height: 1.4;
}

.icon.arrow-right {
  width: 16px;
  height: 16px;
  background-color: transparent;
  position: relative;
}

.icon.arrow-right:after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-45deg);
  width: 8px;
  height: 8px;
  border-right: 2px solid #ccc;
  border-bottom: 2px solid #ccc;
}

/* 页脚区域样式 */
.footer {
  background-color: white;
  padding: 30px;
  border-radius: 10px;
  margin-top: 30px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
}

.footer-content {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.footer-section {
  flex: 1;
}

.footer-logo {
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 10px;
  color: #ff6b81;
}

.footer-desc {
  color: #666;
  line-height: 1.5;
}

.footer-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.footer-info {
  color: #666;
}

.footer-item {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.footer-item .icon {
  margin-right: 10px;
  width: 16px;
  height: 16px;
}

.footer-social {
  display: flex;
  gap: 15px;
}

.social-icon {
  width: 36px;
  height: 36px;
  background-color: #f5f5f5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.social-icon:hover {
  background-color: #ff6b81;
}

.footer-bottom {
  margin-top: 20px;
  text-align: center;
  color: #999;
}

.copyright {
  margin-bottom: 5px;
}

.icp {
  font-size: 12px;
}

.tv-link, .anime-link, .ranking-link {
  text-decoration: none;
  color: inherit;
  display: block;
  width: 100%;
  height: 100%;
}

.ranking-link {
  display: flex;
  align-items: center;
  width: 100%;
}

.tv-series-card:hover, .anime-card:hover, .ranking-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

/* 添加搜索结果区域样式 */
.search-results {
  margin: 20px 0;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 20px;
  position: relative;
}

.search-results .section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.search-results .section-title {
  font-size: 20px;
  color: #333;
  margin: 0;
}

.search-results .close-btn {
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.search-results .close-btn:hover {
  background-color: #d32f2f;
}

.search-results-content {
  margin-bottom: 20px;
}

.search-results .anime-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.search-results .anime-card {
  position: relative;
  transition: transform 0.3s;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-results .anime-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.search-results .anime-link {
  display: block;
  text-decoration: none;
  color: #333;
}

.search-results .anime-cover {
  width: 100%;
  height: 240px;
  object-fit: cover;
  display: block;
}

.search-results .anime-info {
  padding: 10px;
  background-color: white;
}

.search-results .anime-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-results .anime-status {
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
}

.search-results .anime-description {
  font-size: 12px;
  color: #888;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-results .pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.search-results .page-btn {
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s;
  margin: 0 10px;
}

.search-results .page-btn:hover:not(:disabled) {
  background-color: #0056b3;
}

.search-results .page-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.search-results .page-info {
  font-size: 14px;
  color: #666;
}

.search-no-results {
  text-align: center;
  padding: 50px 0;
  color: #666;
  font-size: 16px;
}

/* 反馈弹窗样式 */
.feedback-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.feedback-content {
  width: 400px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.feedback-header {
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.feedback-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
}

.feedback-body {
  padding: 20px;
}

.feedback-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  resize: none;
  font-size: 14px;
}

.contact-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-top: 10px;
  font-size: 14px;
}

.feedback-footer {
  padding: 15px 20px;
  border-top: 1px solid #eee;
  text-align: right;
}

.submit-btn {
  background-color: #ff6b81;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.submit-btn:hover:not(:disabled) {
  background-color: #ff5972;
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* 暗黑主题样式 */
body.dark-theme {
  background-color: #121212;
  color: #e0e0e0;
}

body.dark-theme .main-content {
  background-color: #121212;
}

body.dark-theme .section-title {
  color: #e0e0e0;
}

body.dark-theme .tv-series-card,
body.dark-theme .anime-card,
body.dark-theme .ranking-card {
  background-color: #1e1e1e;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
}

body.dark-theme .tv-title,
body.dark-theme .anime-title,
body.dark-theme .ranking-title {
  color: #e0e0e0;
}

body.dark-theme .tv-description,
body.dark-theme .anime-info,
body.dark-theme .ranking-info {
  color: #aaa;
}

body.dark-theme .control-btn {
  background-color: #333;
  color: #e0e0e0;
}

body.dark-theme .ranking-header {
  border-bottom-color: #333;
}

body.dark-theme .ranking-item {
  border-bottom-color: #333;
}

body.dark-theme .ranking-item:hover {
  background-color: #2a2a2a;
}
</style>
